import { useDatabaseContent } from '@databases/database-state';
import { messages } from '@databases/intl/messages';
import usePanel from '@databases/panel/usePanel';
import { defaultStoreUtils } from '@databases/redux';
import { getService } from '@databases/service-container';
import { currentSnackbarState } from '@databases/snackbar/snackbar';
import {
  getDatabaseEntryOptional,
  isBrokenDatabase
} from '@databases/utils/database/utils';
import { selectDatabaseByContentIdRequired } from '@databases/utils/redux/databases/selectors';
import { useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl-next';
import { isFeatureFlagEnabled } from '@canvas/client-feature-flags';
import { createEntryLinkAttempt } from './entry-link-attempt.ts';

/**
 * This particular hook being so high up in the application causes every child to re-render
 * when the panel state changes. This should be moved.
 *
 * @deprecated This will become an event listener on panel state changes.
 */
export const useEntryPanel = ({ contentId }: { contentId: string }) => {
  const { entryId } = useDatabaseContent();
  const [isLoaded, setIsLoaded] = useState(false);
  const selectionsActions = getService('SelectionsActions');
  const waitForEntry = isFeatureFlagEnabled('databasesEntryLinksRemoteCatchUp');
  const attempt = useMemo(
    () =>
      waitForEntry && entryId
        ? {
            lifecycle: createEntryLinkAttempt(),
            entryId,
            messageId: `linked-database-entry-unavailable:${contentId}:${entryId}`
          }
        : null,
    [contentId, entryId, waitForEntry]
  );

  const { showPanel } = usePanel();

  const intl = useIntl();
  const { formatMessage } = intl;

  const entryExists = defaultStoreUtils.useEqualSelector((state) => {
    const database = selectDatabaseByContentIdRequired(state, { contentId });
    const entry = entryId
      ? database &&
        !isBrokenDatabase(database) &&
        getDatabaseEntryOptional(database, entryId)
      : null;

    return !!entry;
  });

  useLayoutEffect(() => {
    if (!attempt) {
      return;
    }
    return () => {
      attempt.lifecycle.stop();
      currentSnackbarState.clearMessage(attempt.messageId);
    };
  }, [attempt]);

  useEffect(() => {
    if (waitForEntry) {
      if (!attempt) {
        return;
      }
      attempt.lifecycle.start({
        entryExists,
        onOpen: () => {
          showPanel('EntryViewPanel', { entryId: attempt.entryId });
          selectionsActions.selectEntries([attempt.entryId]);
        },
        onUnavailable: () => {
          currentSnackbarState.setMessage(
            formatMessage(messages['snackbar-error.deleted-linked-entry']),
            {
              id: attempt.messageId,
              type: 'info',
              showCloseButton: true
            }
          );
        }
      });
      return attempt.lifecycle.stop;
    }
    if (!isLoaded && entryId && entryExists) {
      showPanel('EntryViewPanel', {
        entryId
      });

      selectionsActions.selectEntries([entryId]);
    } else if (entryId && !entryExists) {
      void currentSnackbarState.setMessage(
        formatMessage(messages['snackbar-error.deleted-linked-entry']),
        {
          id: 'linked-database-entry-deleted',
          type: 'info',
          showCloseButton: true
        }
      );
    }
    setIsLoaded(true);
  }, [
    attempt,
    waitForEntry,
    entryExists,
    entryId,
    formatMessage,
    isLoaded,
    selectionsActions,
    showPanel
  ]);
};
